import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { createPortalApi, acquirePortalToken } from '../api/portalApi';
import { useRuntimeConfig } from '../context/RuntimeConfigContext';
import { Icon, Chevron } from '../components/icons';
import EmptyState from '../components/EmptyState';
import Spinner from '../components/Spinner';
import Modal from '../components/Modal';
import AutomationHeroIllustration from '../components/illustrations/AutomationHeroIllustration';
import { FINT_EXTENSION_ID } from '../constants/extension';
import type { ReviewSession, TestCase } from '../types';

function formatDate(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function timeAgo(iso?: string) {
  if (!iso) return '—';
  const diff  = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (days  > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins  > 0) return `${mins}m ago`;
  return 'just now';
}

const STATUS_LABEL: Record<string, string> = {
  in_review: 'In Review',
  approved:  'Approved',
  pushed:    'Pushed',
  partial:   'Partial',
};

interface RecordModal {
  tc: TestCase;
  url: string;
  launching: boolean;
  error: string;
}

interface McpModal {
  tc: TestCase;
  sessionId: string;
  url: string;
  starting: boolean;
  error: string;
}

export default function WebAutomationPage() {
  const { instance } = useMsal();
  const cfg          = useRuntimeConfig();
  const navigate     = useNavigate();
  const api          = createPortalApi(instance, cfg.msalClientId, cfg.apiBaseUrl);

  const [sessions, setSessions]           = useState<ReviewSession[]>([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');
  const [expandedId, setExpandedId]       = useState<string | null>(null);
  const [casesMap, setCasesMap]           = useState<Record<string, TestCase[]>>({});
  const [loadingCases, setLoadingCases]   = useState<string | null>(null);
  const [modal, setModal]                 = useState<RecordModal | null>(null);
  const [mcpModal, setMcpModal]           = useState<McpModal | null>(null);
  const [statusFilter, setStatusFilter]   = useState('');
  const [authorFilter, setAuthorFilter]   = useState('');
  const [search, setSearch]               = useState('');

  useEffect(() => {
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function load() {
    try {
      const all = await api.getSessions(false);
      const automated = all.filter(s => !!s.automatedAt);
      automated.sort((a, b) => new Date(b.automatedAt!).getTime() - new Date(a.automatedAt!).getTime());
      setSessions(automated);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function toggleSession(sessionId: string) {
    if (expandedId === sessionId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(sessionId);
    if (casesMap[sessionId]) return;
    setLoadingCases(sessionId);
    try {
      const session = await api.getSession(sessionId);
      setCasesMap(prev => ({ ...prev, [sessionId]: session.cases ?? [] }));
    } catch {
      setCasesMap(prev => ({ ...prev, [sessionId]: [] }));
    } finally {
      setLoadingCases(null);
    }
  }

  function openRecordModal(tc: TestCase) {
    setModal({ tc, url: '', launching: false, error: '' });
  }

  async function launchRecording() {
    if (!modal) return;
    const { tc, url } = modal;
    if (!url.trim()) {
      setModal(m => m ? { ...m, error: 'Please enter a target URL.' } : m);
      return;
    }

    setModal(m => m ? { ...m, launching: true, error: '' } : m);
    try {
      const authToken = await acquirePortalToken(instance, cfg.msalClientId);
      await sendToExtension(FINT_EXTENSION_ID, {
        type: 'OPEN_FOR_RECORDING',
        targetUrl: url.trim(),
        testCase: { title: tc.title, steps: tc.steps, expectedResult: tc.expectedResult },
        authToken,
      });
      setModal(null);
    } catch (e: any) {
      setModal(m => m ? {
        ...m,
        launching: false,
        error: `Could not reach the extension: ${e?.message || 'unknown error'}. Make sure the Fint QualiForge Test Recorder extension is installed and enabled.`,
      } : m);
    }
  }

  function openMcpModal(tc: TestCase, sessionId: string) {
    setMcpModal({ tc, sessionId, url: '', starting: false, error: '' });
  }

  async function startMcpGeneration() {
    if (!mcpModal) return;
    const { tc, sessionId, url } = mcpModal;
    if (!url.trim()) {
      setMcpModal(m => m ? { ...m, error: 'Please enter a target URL.' } : m);
      return;
    }

    setMcpModal(m => m ? { ...m, starting: true, error: '' } : m);
    try {
      const { jobId } = await api.startMcpGeneration(sessionId, tc.id, url.trim());
      setMcpModal(null);
      navigate(`/web-automation/mcp/${jobId}`);
    } catch (e: any) {
      setMcpModal(m => m ? { ...m, starting: false, error: e?.message || 'Failed to start generation.' } : m);
    }
  }

  function sendToExtension(extId: string, message: object): Promise<void> {
    return new Promise((resolve, reject) => {
      const cr = (window as any).chrome?.runtime;
      if (!cr?.sendMessage) { reject(new Error('chrome.runtime not available')); return; }
      cr.sendMessage(extId, message, (resp: any) => {
        if (cr.lastError) { reject(new Error(cr.lastError.message || 'Chrome runtime error')); return; }
        if (!resp?.success) { reject(new Error('Extension returned no response')); return; }
        resolve();
      });
    });
  }

  if (loading) return <div className="page-loading"><span className="spinner" />Loading…</div>;
  if (error)   return <div className="page-error">Failed to load: {error}</div>;

  const uniqueAuthors = Array.from(
    new Map(sessions.map(s => [s.authorOid, s.authorName || s.authorEmail])).entries(),
  ).sort((a, b) => a[1].localeCompare(b[1]));

  const filtered = sessions.filter(s => {
    if (statusFilter && s.status !== statusFilter) return false;
    if (authorFilter && s.authorOid !== authorFilter) return false;
    if (search && !s.title.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const hasFilter = !!(statusFilter || authorFilter || search);

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Web Automation</h1>
        <p className="page-subtitle">
          Sessions whose approved test cases have been sent to automation. Expand a session to record automation code for individual test cases.
        </p>
      </div>

      {sessions.length === 0 ? (
        <EmptyState illustration={<AutomationHeroIllustration />}>
          No sessions have been automated yet. Use the <strong>Automate</strong> button on the Dashboard to send approved test cases here.
        </EmptyState>
      ) : (
        <>
          <div className="filter-bar">
            <label className="filter-label">Status</label>
            <select className="filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              {Object.entries(STATUS_LABEL).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>

            <label className="filter-label">Author</label>
            <select className="filter-select" value={authorFilter} onChange={e => setAuthorFilter(e.target.value)}>
              <option value="">All</option>
              {uniqueAuthors.map(([oid, name]) => (
                <option key={oid} value={oid}>{name}</option>
              ))}
            </select>

            <input
              className="form-input web-auto-search"
              type="search"
              placeholder="Search by session name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />

            {hasFilter && (
              <button
                className="btn btn-sm btn-outline"
                onClick={() => { setStatusFilter(''); setAuthorFilter(''); setSearch(''); }}
              >
                Clear filters
              </button>
            )}
            <span className="filter-count">{filtered.length} session{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {filtered.length === 0 ? (
            <EmptyState illustration={<AutomationHeroIllustration />}>
              No automated sessions match the selected filters.
            </EmptyState>
          ) : (
            <div className="sessions-table-wrapper">
              <table className="sessions-table web-auto-table">
                <colgroup>
                  <col className="web-auto-col-session" />
                  <col className="web-auto-col-author" />
                  <col className="web-auto-col-cases" />
                  <col className="web-auto-col-activity" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Session</th>
                    <th>Author</th>
                    <th>Approved Cases</th>
                    <th>Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(s => (
                    <>
                      <tr
                        key={s.sessionId}
                        className="session-row"
                        onClick={() => toggleSession(s.sessionId)}
                        style={{ cursor: 'pointer' }}
                      >
                        <td>
                          <div className="web-auto-session-cell">
                            <span className="web-auto-expand-btn" aria-label={expandedId === s.sessionId ? 'Collapse' : 'Expand'}>
                              <Chevron open={expandedId === s.sessionId} size={18} />
                            </span>
                            <span className="session-name">{s.title}</span>
                            <span className="web-auto-type-pill">{s.outputType}</span>
                          </div>
                        </td>
                        <td>
                          <div className="web-auto-author-cell">
                            <span className="avatar" title={s.authorEmail}>
                              {(s.authorName || s.authorEmail)[0].toUpperCase()}
                            </span>
                            <span className="author-name" title={s.authorEmail}>{s.authorName || s.authorEmail}</span>
                          </div>
                        </td>
                        <td className="tc-count" onClick={e => e.stopPropagation()}>
                          <button
                            className="btn btn-sm btn-outline web-auto-view-btn"
                            onClick={() => navigate(`/sessions/${s.sessionId}`)}
                          >
                            View Testcases ({s.approvedCount})
                          </button>
                        </td>
                        <td className="session-date">
                          <div className="web-auto-activity-cell">
                            <span title={formatDate(s.createdAt)}>Created: {timeAgo(s.createdAt)}</span>
                            <span title={formatDate(s.automatedAt)}>Sent to automation: {timeAgo(s.automatedAt)}</span>
                          </div>
                        </td>
                      </tr>

                      {expandedId === s.sessionId && (
                        <tr key={`${s.sessionId}-cases`}>
                          <td colSpan={4} className="web-auto-subtable-cell">
                            {loadingCases === s.sessionId ? (
                              <div className="web-auto-subtable-msg">
                                <Spinner size="sm" /> Loading test cases…
                              </div>
                            ) : (casesMap[s.sessionId] ?? []).length === 0 ? (
                              <div className="web-auto-subtable-msg">
                                No approved test cases found for this session.
                              </div>
                            ) : (
                              <table className="web-auto-subtable">
                                <thead>
                                  <tr>
                                    <th className="web-auto-subtable-th">Test Case</th>
                                    <th className="web-auto-subtable-th">Type</th>
                                    <th className="web-auto-subtable-th">Steps</th>
                                    <th className="web-auto-subtable-th web-auto-subtable-th-center">Record</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(casesMap[s.sessionId] ?? [])
                                    .filter(tc => tc.status === 'approved' || tc.status === 'pushed')
                                    .map(tc => (
                                      <tr key={tc.id}>
                                        <td className="web-auto-subtable-td">
                                          <span style={{ fontWeight: 500 }}>{tc.title}</span>
                                        </td>
                                        <td className="web-auto-subtable-td web-auto-subtable-td-muted">
                                          {tc.type}
                                        </td>
                                        <td className="web-auto-subtable-td web-auto-subtable-td-muted">
                                          {tc.steps.length} step{tc.steps.length !== 1 ? 's' : ''}
                                        </td>
                                        <td className="web-auto-subtable-td web-auto-subtable-td-center">
                                          <div className="web-auto-record-actions">
                                            <button
                                              className="btn btn-sm btn-primary"
                                              onClick={() => openRecordModal(tc)}
                                              title="Open Automation Coder in the extension to generate code for this test case"
                                            >
                                              <Icon name="zap" size={13} /> Code
                                            </button>
                                            <button
                                              className="btn btn-sm btn-ai"
                                              onClick={() => openMcpModal(tc, s.sessionId)}
                                              title="Drive a live browser with an AI Generator/Healer agent to write and self-verify Playwright code for this test case"
                                            >
                                              <Icon name="sparkle" size={13} /> Generate Code with MCP
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* URL input modal */}
      {modal && (
        <Modal
          title="Record Automation Code"
          onClose={() => setModal(null)}
          width={460}
          footer={
            <>
              <button className="btn btn-outline" onClick={() => setModal(null)} disabled={modal.launching}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={launchRecording} disabled={modal.launching}>
                {modal.launching ? 'Opening…' : <><Icon name="zap" size={13} /> Open Automation Coder</>}
              </button>
            </>
          }
        >
          <div className="stack-gap-sm">
            <p className="mb-sm"><strong>{modal.tc.title}</strong></p>
            <p className="hint-text mb-sm">
              Opens a new tab at the URL you enter and activates the Automation Coder in the extension side panel, pre-filled with this test case.
            </p>
            <div className="form-group">
              <label>Target app URL</label>
              <input
                className="form-input"
                type="url"
                placeholder="https://your-app.example.com"
                value={modal.url}
                onChange={e => setModal(m => m ? { ...m, url: e.target.value, error: '' } : m)}
                onKeyDown={e => { if (e.key === 'Enter') launchRecording(); }}
                autoFocus
              />
            </div>
            {modal.error && (
              <p style={{ margin: 0, fontSize: 12, color: 'var(--danger)' }}>{modal.error}</p>
            )}
          </div>
        </Modal>
      )}

      {/* MCP codegen URL input modal */}
      {mcpModal && (
        <Modal
          title="Generate Code with MCP"
          onClose={() => setMcpModal(null)}
          width={460}
          footer={
            <>
              <button className="btn btn-outline" onClick={() => setMcpModal(null)} disabled={mcpModal.starting}>
                Cancel
              </button>
              <button className="btn btn-ai" onClick={startMcpGeneration} disabled={mcpModal.starting}>
                {mcpModal.starting ? 'Starting…' : <><Icon name="sparkle" size={13} /> Start Generation</>}
              </button>
            </>
          }
        >
          <div className="stack-gap-sm">
            <p className="mb-sm"><strong>{mcpModal.tc.title}</strong></p>
            <p className="hint-text mb-sm">
              A live Generator agent will drive a real browser at the URL below to write and verify
              Playwright code for this test case, then a Healer agent runs it for real and self-fixes
              any failures. This can take a few minutes and calls out to your organisation's Claude API key.
            </p>
            <div className="form-group">
              <label>Target app URL</label>
              <input
                className="form-input"
                type="url"
                placeholder="https://your-app.example.com"
                value={mcpModal.url}
                onChange={e => setMcpModal(m => m ? { ...m, url: e.target.value, error: '' } : m)}
                onKeyDown={e => { if (e.key === 'Enter') startMcpGeneration(); }}
                autoFocus
              />
            </div>
            {mcpModal.error && (
              <p style={{ margin: 0, fontSize: 12, color: 'var(--danger)' }}>{mcpModal.error}</p>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
